// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import NuxChecklistCelebration from 'app/common/react/components/growth/activation/NuxChecklist/NuxChecklistCelebration';
import PropTypes from 'prop-types';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { State } from 'app/common/redux/types';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import NuxChecklistAction, {
  type NuxChecklistActionProps,
} from 'app/common/react/components/growth/activation/NuxChecklist/NuxChecklistAction';
import { Box, Column, Heading, Sticky, IconButton } from 'gestalt';

const NuxChecklistPropTypes = {
  handleComplete: PropTypes.func.isRequired,
  handleDismiss: PropTypes.func.isRequired,
  initialDidPinClickthrough: PropTypes.bool.isRequired,
  initialDidPinCloseup: PropTypes.bool.isRequired,
  initialDidPinSave: PropTypes.bool.isRequired,
  didPinClickthrough: PropTypes.bool.isRequired,
  didPinCloseup: PropTypes.bool.isRequired,
  didPinSave: PropTypes.bool.isRequired,
  updatePinActions: PropTypes.func.isRequired,
};

type NuxChecklistProps = {|
  checklistExpGroup: string,
  handleComplete: () => void,
  handleDismiss: () => void,
  initialDidPinClickthrough: boolean,
  initialDidPinCloseup: boolean,
  initialDidPinSave: boolean,
  didPinClickthrough: boolean,
  didPinCloseup: boolean,
  didPinSave: boolean,
  updatePinActions: (payload: {
    didPinClickthrough: boolean,
    didPinCloseup: boolean,
    didPinSave: boolean,
  }) => void,
|};

type NuxChecklistState = {|
  showCelebration: boolean,
  fadeChecklistOut: boolean,
|};

const fadeOutStyles = () => ({
  __style: {
    transition: 'opacity 0.4s ease',
    opacity: 0,
    backgroundColor: 'white',
  },
});

export function getUserStatePinActions(userId: string): Promise<*> {
  return new Promise((resolve, reject) =>
    ResourceFactory.create('UserResource', {
      user_id: userId,
      field_set_key: 'pin_actions',
    })
      .callGet()
      .then(resolve)
      .catch(reject),
  );
}

export function completeUserStatePinAction(
  state: 'HAS_DONE_CLICKTHROUGH' | 'HAS_DONE_CLOSEUP',
): Promise<*> {
  return new Promise((resolve, reject) =>
    ResourceFactory.create('UserStateResource', { state, value: 1 })
      .callCreate()
      .then(resolve)
      .catch(reject),
  );
}

export class NuxChecklist extends React.PureComponent<NuxChecklistProps, NuxChecklistState> {
  static propTypes = NuxChecklistPropTypes;

  constructor(props: NuxChecklistProps) {
    super(props);
    this.state = {
      showCelebration: false,
      fadeChecklistOut: false,
    };
    const {
      initialDidPinClickthrough,
      initialDidPinCloseup,
      initialDidPinSave,
      updatePinActions,
    } = props;
    let { didPinClickthrough, didPinCloseup, didPinSave } = props;

    // initial pin actions from server-side are more accurate on page reload
    // ex of when this isn't true: new user does not initially have closeup completed
    // they click on closeup and click back (this is not a full page reload)
    // redux store info is more accurate in this case
    if (initialDidPinClickthrough) {
      didPinClickthrough = true;
    }
    if (initialDidPinCloseup) {
      didPinCloseup = true;
    }
    if (initialDidPinSave) {
      didPinSave = true;
    }

    updatePinActions({ didPinClickthrough, didPinCloseup, didPinSave });
  }

  startCelebrationAnimation() {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        this.setState({ fadeChecklistOut: true });
        window.requestAnimationFrame(() => {
          this.setState({ showCelebration: true });
        });
      });
    }, this.celebrationAnimationDelayMS);
  }

  componentDidMount() {
    const {
      handleComplete,
      initialDidPinClickthrough,
      initialDidPinCloseup,
      initialDidPinSave,
      checklistExpGroup,
    } = this.props;
    let { didPinClickthrough, didPinCloseup, didPinSave } = this.props;

    if (initialDidPinClickthrough) {
      didPinClickthrough = true;
    }
    if (initialDidPinCloseup) {
      didPinCloseup = true;
    }
    if (initialDidPinSave) {
      didPinSave = true;
    }

    const allActionsCompleted = didPinClickthrough && didPinCloseup && didPinSave;
    if (allActionsCompleted) {
      const inCelebrationAnimationExp =
        checklistExpGroup === 'check_celebration_animation' ||
        checklistExpGroup === 'celebration_animation' ||
        checklistExpGroup === 'enabled';
      if (inCelebrationAnimationExp) {
        this.startCelebrationAnimation();
      } else {
        setTimeout(handleComplete, this.delayBeforeCompletionMS);
      }
    }
  }

  delayBeforeCompletionMS = 5000;
  celebrationAnimationDelayMS = 3500;

  render() {
    const {
      didPinClickthrough,
      didPinCloseup,
      didPinSave,
      handleDismiss,
      checklistExpGroup,
      handleComplete,
    } = this.props;
    const { showCelebration, fadeChecklistOut } = this.state;
    const inCheckAnimationExp =
      checklistExpGroup === 'check_animation' ||
      checklistExpGroup === 'check_celebration_animation' ||
      checklistExpGroup === 'enabled';
    // Show the heading text for the completed action farthest on the right (hard-code)
    // UI Visualization: [x]Pin Closeup, [ ]Pin Clickthrough, [x]Pin Save
    let headingText;
    const allActionsCompleted = didPinClickthrough && didPinCloseup && didPinSave;
    if (allActionsCompleted) {
      headingText = i18n._(
        "You're ready! Keep exploring to find your next great idea",
        'user completed list of actions below the header',
      );
    } else if (didPinSave) {
      headingText = i18n._(
        'Saved! Each Pin you save improves your feed',
        'user completed a pin save and the header text is changing',
      );
    } else if (didPinClickthrough) {
      headingText = i18n._(
        'See! Every Pin links to the website it came from',
        'user completed a pin clickthrough and the header text is changing',
      );
    } else if (didPinCloseup) {
      headingText = i18n._(
        'Nice find! You can click any Pin to learn more about it',
        'user completed a closeup and the header text is changing',
      );
    } else {
      headingText = i18n._(
        "Here's how to get the most out of Pinterest",
        'user has a list of actions below the header to complete',
      );
    }

    const checklistActions = [
      {
        name: 'closeup',
        accessibilityLabel: 'closeup',
        icon: 'arrow-up-right',
        text: i18n._(
          'Click on a Pin you like',
          'explaining what to do to complete the closeup step of checklist',
        ),
        isCompleted: didPinCloseup,
      },
      {
        name: 'repin',
        accessibilityLabel: 'save',
        icon: 'pin',
        text: i18n._(
          'Save it for later',
          'explaining what to do to complete the pin save step of checklist',
        ),
        isCompleted: didPinSave,
      },
      {
        name: 'clickthrough',
        accessibilityLabel: 'link',
        icon: 'globe',
        text: i18n._(
          "Visit the link it's from ",
          'explaining what to do to complete the clickthrough step of checklist',
        ),
        isCompleted: didPinClickthrough,
      },
    ];

    return (
      <Sticky dangerouslySetZIndex={{ __zIndex: 2 }} top={60}>
        {!showCelebration ? (
          <Box dangerouslySetInlineStyle={fadeChecklistOut ? fadeOutStyles() : null}>
            <Box
              color="lightGray"
              display="flex"
              dangerouslySetInlineStyle={{ __style: { overflow: 'hidden' } }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                paddingX={12}
                paddingY={8}
                color="blue"
                maxWidth={550}
                width="100%"
                dangerouslySetInlineStyle={{ __style: { borderTopRightRadius: '80px 380px' } }}
              >
                <Heading size="sm" color="white" overflow="normal">
                  {headingText}
                </Heading>
              </Box>
              <Box position="absolute" top right paddingY={2} paddingX={5}>
                <IconButton
                  accessibilityLabel={i18n._(
                    'Hide Checklist',
                    'hide the checklist of pin actions at the top of the homefeed',
                  )}
                  icon="cancel"
                  onClick={handleDismiss}
                  size="sm"
                />
              </Box>
              <Box width="100%" display="flex" alignItems="center" justifyContent="center">
                <Box
                  display="flex"
                  padding={4}
                  marginStart={-4}
                  marginEnd={-4}
                  marginTop={-2}
                  marginBottom={-2}
                  maxWidth={900}
                  width="100%"
                  wrap
                >
                  {checklistActions.map(
                    (
                      {
                        name,
                        icon,
                        accessibilityLabel,
                        text,
                        isCompleted,
                      }: NuxChecklistActionProps,
                      idx: number,
                    ) => (
                      <Column key={text} lgSpan={4} span={12}>
                        <Box display="flex" justifyContent="center" paddingX={4} paddingY={2}>
                          {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                          {/* eslint-disable jsx-a11y/no-static-element-interactions */}
                          <div onClick={() => trackRegisterAction(`checklist.${name}.click`)}>
                            <NuxChecklistAction
                              name={name}
                              idx={idx}
                              accessibilityLabel={accessibilityLabel}
                              icon={icon}
                              text={text}
                              isCompleted={isCompleted}
                              inCheckAnimationExp={inCheckAnimationExp}
                            />
                          </div>
                          {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                          {/* eslint-enable jsx-a11y/no-static-element-interactions */}
                        </Box>
                      </Column>
                    ),
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <NuxChecklistCelebration handleComplete={handleComplete} />
        )}
      </Sticky>
    );
  }
}

// $FlowFixMe: Please add "import type { Dispatch } from 'redux';"
const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  updatePinActions: (payload) => dispatch({ type: 'UPDATE_PIN_ACTIONS', payload }),
});

const mapStateToProps = ({
  ui: {
    checklist: { didPinCloseup, didPinClickthrough, didPinSave },
  },
}: State) => ({ didPinCloseup, didPinClickthrough, didPinSave });

const ChecklistWithAnimationExperiment = (props: NuxChecklistProps) => (
  <Experiment name="web_checklist_check_celebration_animation">
    {({ group }) => <NuxChecklist {...props} checklistExpGroup={group} />}
  </Experiment>
);

export default compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(ChecklistWithAnimationExperiment);
